import React, { useState, useEffect } from 'react';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
  sendEmailVerification, // Import for email verification
  signOut // Import for sign out
} from 'firebase/auth';
import { app } from './firebaseConfig'; // Assuming firebaseConfig.js is in the same directory

// Get the Firebase Auth instance
const auth = getAuth(app);

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true); // Toggle between Login and Sign Up views
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState(''); // For sign-up password confirmation
  const [error, setError] = useState(''); // To display errors to the user
  const [loading, setLoading] = useState(false); // To show loading state and disable buttons
  const [user, setUser] = useState(null); // To keep track of the currently authenticated user
  const [showModal, setShowModal] = useState(false); // For custom modal messages
  const [modalMessage, setModalMessage] = useState(''); // Content for the custom modal

  // Effect hook to listen for authentication state changes
  // This will run once on component mount and whenever the user's auth state changes (login, logout)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser); // Set the current user
      setLoading(false); // Ensure loading is false after initial check
    });
    // Cleanup function to unsubscribe from the listener when the component unmounts
    return () => unsubscribe();
  }, []); // Empty dependency array means this effect runs only once on mount

  // Function to display a custom modal message to the user
  const showCustomModal = (message) => {
    setModalMessage(message);
    setShowModal(true);
  };

  // Function to close the custom modal
  const closeCustomModal = () => {
    setShowModal(false);
    setModalMessage('');
    setError(''); // Clear any errors when modal is closed
  };

  // Handles both email/password sign-up and login
  const handleAuth = async (e) => {
    e.preventDefault(); // Prevent default form submission behavior
    setError(''); // Clear any previous errors
    setLoading(true); // Indicate loading state

    // Basic form validation
    if (!email || !password) {
      setError('Please enter both email and password.');
      setLoading(false);
      return;
    }

    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    try {
      if (isLogin) {
        // Attempt to sign in an existing user
        await signInWithEmailAndPassword(auth, email, password);
        console.log('User signed in successfully!');
        showCustomModal('Login successful! Welcome back.');
        // In a real app, you would redirect the user to a dashboard or home page here
      } else {
        // Attempt to create a new user account
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        console.log('User created successfully!', userCredential.user);

        // Send email verification to the newly created user
        await sendEmailVerification(userCredential.user);
        console.log('Verification email sent!');
        showCustomModal('Sign up successful! A verification email has been sent to your inbox. Please verify your email to continue.');
        // In a real app, you might redirect to a "check your email" page
      }
    } catch (err) {
      // Catch and handle Firebase authentication errors
      console.error('Firebase Auth Error:', err.code, err.message);
      let errorMessage = 'An unexpected error occurred. Please try again.';
      switch (err.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'Email already in use. Try logging in or use a different email.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address format.';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password is too weak (minimum 6 characters).';
          break;
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          errorMessage = 'Invalid email or password.';
          break;
        case 'auth/too-many-requests':
          // This is the specific rate-limiting error you've been encountering
          errorMessage = 'Too many attempts. Please try again later. If the issue persists, please wait 5 minutes.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your internet connection.';
          break;
        default:
          errorMessage = `Authentication failed: ${err.message}`;
          break;
      }
      setError(errorMessage); // Set the error message to display
    } finally {
      setLoading(false); // End loading state regardless of success or failure
    }
  };

  // Handles Google social sign-in
  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    const provider = new GoogleAuthProvider(); // Create a new Google Auth Provider instance
    try {
      await signInWithPopup(auth, provider); // Open Google sign-in popup
      console.log('Signed in with Google successfully!');
      showCustomModal('Signed in with Google successfully!');
      // In a real app, you would redirect after successful social login
    } catch (err) {
      console.error('Google Sign-In Error:', err.code, err.message);
      let errorMessage = `Google sign-in failed: ${err.message}`;
      if (err.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Google sign-in window was closed.';
      } else if (err.code === 'auth/cancelled-popup-request') {
        errorMessage = 'Another sign-in popup was already open. Please close other popups.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handles Facebook social sign-in
  // IMPORTANT: Requires careful setup in Firebase Console and Facebook Developer Console
  const handleFacebookSignIn = async () => {
    setError('');
    setLoading(true);
    const provider = new FacebookAuthProvider(); // Create a new Facebook Auth Provider instance
    try {
      await signInWithPopup(auth, provider); // Open Facebook sign-in popup
      console.log('Signed in with Facebook successfully!');
      showCustomModal('Signed in with Facebook successfully!');
      // In a real app, you would redirect after successful social login
    } catch (err) {
      console.error('Facebook Sign-In Error:', err.code, err.message);
      let errorMessage = `Facebook sign-in failed: ${err.message}`;
      if (err.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Facebook sign-in window was closed.';
      } else if (err.code === 'auth/cancelled-popup-request') {
        errorMessage = 'Another sign-in popup was already open. Please close other popups.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Custom Modal Component: Used instead of alert() or confirm()
  const CustomModal = ({ message, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm mx-auto text-center">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Notification</h3>
        <p className="text-gray-600 mb-6">{message}</p>
        <button
          onClick={onClose}
          className="py-2 px-4 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75 transition duration-200 ease-in-out rounded-md"
        >
          Close
        </button>
      </div>
    </div>
  );

  // If a user is already authenticated, display a welcome message and sign-out option
  if (user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4 font-sans">
        <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md text-center">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Welcome, {user.email || user.displayName || 'User'}!</h2>
          {user.emailVerified ? (
            <p className="text-green-600 mb-6">Your email is verified!</p>
          ) : (
            <p className="text-yellow-600 mb-6">Your email is NOT verified. Please check your inbox for a verification link.</p>
          )}
          <p className="text-gray-600 mb-6">You are already signed in.</p>
          <button
            onClick={() => {
              signOut(auth); // Sign out the user
              showCustomModal('You have been signed out.'); // Show sign-out confirmation
            }}
            className="w-full py-2 px-4 bg-red-600 text-white font-semibold rounded-md shadow-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-75 transition duration-200 ease-in-out rounded-md"
          >
            Sign Out
          </button>
        </div>
        {/* Render modal if showModal is true */}
        {showModal && <CustomModal message={modalMessage} onClose={closeCustomModal} />}
      </div>
    );
  }

  // Render the authentication form (Login or Sign Up)
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4 font-sans">
      <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
          {isLogin ? 'Login to Your Account' : 'Create Your Account'}
        </h2>

        <form onSubmit={handleAuth} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              id="email"
              className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition duration-150 ease-in-out rounded-md"
              placeholder="email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              aria-label="Email Address"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password (min 6 characters)</label>
            <input
              type="password"
              id="password"
              className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition duration-150 ease-in-out rounded-md"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              aria-label="Password"
            />
          </div>

          {!isLogin && (
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input
                type="password"
                id="confirm-password"
                className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition duration-150 ease-in-out rounded-md"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                aria-label="Confirm Password"
              />
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          <button
            type="submit"
            className="w-full py-2 px-4 bg-orange-600 text-white font-semibold rounded-md shadow-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-75 transition duration-200 ease-in-out"
            disabled={loading}
          >
            {loading ? 'Processing...' : (isLogin ? 'Login' : 'Sign Up')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            {isLogin ? "Don't have an account?" : "Already have an account?"}{' '}
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-blue-600 hover:text-blue-800 font-medium focus:outline-none transition duration-150 ease-in-out"
            >
              {isLogin ? 'Sign Up' : 'Login'}
            </button>
          </p>
        </div>

        <div className="mt-6 space-y-3">
          {/* Google Sign-In Button */}
          <button
            onClick={handleGoogleSignIn}
            className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-gray-700 font-semibold hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 ease-in-out"
            disabled={loading}
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
              <path d="M12.24 10.27v2.44h6.63c-.29 1.76-2.12 4.43-6.63 4.43-3.99 0-7.24-3.25-7.24-7.24S8.25 2.76 12.24 2.76c2.14 0 3.56.89 4.38 1.68l2.09-2.09C17.06 1.74 14.88 1 12.24 1 6.84 1 2.39 5.45 2.39 10.84s4.45 9.84 9.85 9.84c5.44 0 9.47-3.95 9.47-9.35 0-.64-.09-1.25-.2-1.84h-9.27z"/>
            </svg>
            {loading ? '...' : 'Sign in with Google'}
          </button>

          {/* Facebook Sign-In Button (Requires additional setup in Firebase and Facebook Dev Console) */}
          <button
            onClick={handleFacebookSignIn}
            className="w-full flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-gray-700 font-semibold hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200 ease-in-out"
            disabled={loading}
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
              <path d="M18.995 0h-13.99c-2.76 0-5.005 2.245-5.005 5.005v13.99c0 2.76 2.245 5.005 5.005 5.005h7.228v-9.018h-3.033v-3.593h3.033v-2.665c0-3.003 1.833-4.646 4.512-4.646 1.3 0 2.417.097 2.74.14v3.177h-1.884c-1.485 0-1.776.705-1.776 1.748v2.296h3.559l-.576 3.593h-2.983v9.018h4.436c2.76 0 5.005-2.245 5.005-5.005V5.005c0-2.76-2.245-5.005-5.005-5.005z"/>
            </svg>
            {loading ? '...' : 'Sign in with Facebook'}
          </button>
        </div>
      </div>
      {showModal && <CustomModal message={modalMessage} onClose={closeCustomModal} />}
    </div>
  );
};

export default AuthPage;
